import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import logo from '../assets/krishidnya-logo.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const navLinks = [
    {
      name: 'Home',
      path: '/',
      title: 'Krishidnya - Smart Agricultural Technology & Sustainable Farming Solutions India',
      description: 'Premium agricultural technology solutions, modern farming equipment, and sustainable crop management services for Indian farmers',
      keywords: 'agricultural technology, smart farming, sustainable agriculture, crop management'
    },
    {
      name: 'Products',
      path: '/Products',
      title: 'Agricultural Products - Premium Farming Equipment, Tools & Solutions | Krishidnya',
      description: 'Explore our premium range of agricultural products including smart farming equipment, irrigation systems, crop protection solutions, and sustainable farming tools for modern agriculture',
      keywords: 'agricultural products, farming equipment, irrigation systems, crop protection, farming tools, agritech products'
    },
    {
      name: 'Services',
      path: '/services',
      title: 'Agricultural Services - Precision Farming, Crop Consulting & Soil Testing India',
      description: 'Expert agricultural consulting services including precision farming, soil health analysis, crop disease management, and irrigation solutions',
      keywords: 'agricultural services, precision farming, crop consulting, soil testing'
    },
    {
      name: 'Vision',
      path: '/vision',
      title: 'Our Vision - Revolutionary Agricultural Innovation & Future of Sustainable Farming',
      description: 'Building the future of sustainable agriculture through AI-powered farming, IoT sensors, and eco-friendly agricultural practices',
      keywords: 'agricultural innovation, future farming, sustainable agriculture, agritech vision'
    },
    {
      name: 'About',
      path: '/about',
      title: 'About Krishidnya - Leading Agricultural Experts & Farming Technology Pioneers',
      description: 'Meet our team of agricultural scientists, agronomists, and farming technology experts dedicated to revolutionizing Indian agriculture',
      keywords: 'agricultural experts, agronomy team, farming specialists, agritech company'
    },
    {
      name: 'Careers',
      path: '/careers',
      title: 'Agricultural Careers - Join Our Team of Farming Technology Experts',
      description: 'Explore exciting career opportunities in agricultural technology, research, and sustainable farming innovation',
      keywords: 'agricultural careers, agritech jobs, farming jobs, agriculture employment'
    },
    {
      name: 'Contact',
      path: '/contact',
      title: 'Contact Agricultural Experts - Free Farming Consultation & Support 24/7',
      description: 'Get instant support from agricultural experts, schedule farm visits, and receive personalized crop management solutions',
      keywords: 'agricultural consultation, farming support, crop advisory, agriculture helpline'
    },
  ];

  return (
    <>
      {/* Enhanced Structured Data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Krishidnya Agricultural Solutions",
          "alternateName": "Krishidnya Agritech",
          "url": "https://www.krishidnya.com",
          "logo": "https://www.krishidnya.com/logo.png",
          "description": "Leading agricultural technology company providing innovative farming solutions, precision agriculture, crop management services, and sustainable farming practices for modern Indian farmers",
          "foundingDate": "2020",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "India"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+91-1234567890",
            "contactType": "Customer Service",
            "availableLanguage": ["English", "Hindi", "Marathi"]
          },
          "sameAs": [
            "https://www.facebook.com/krishidnya",
            "https://twitter.com/krishidnya",
            "https://www.linkedin.com/company/krishidnya",
            "https://www.instagram.com/krishidnya"
          ],
          "keywords": "agricultural technology, precision farming, sustainable agriculture, crop management, soil testing, farming equipment, agricultural consulting, agritech solutions, smart farming, organic farming"
        })}
      </script>

      {/* Hidden H1 for Homepage SEO */}
      {location.pathname === '/' && (
        <h1 className="visually-hidden">
          Krishidnya Agricultural Solutions - Empowering Farmers With Technology | Smart Farming Solutions | Sustainable Agriculture Experts | Precision Farming Technology | Agricultural Consulting Services
        </h1>
      )}

      <header
        className={`header ${isScrolled ? 'scrolled' : ''}`}
        role="banner"
        itemScope
        itemType="https://schema.org/Organization"
      >
        <div className="header-container">
          <nav className="navbar" role="navigation" aria-label="Primary Navigation - Agricultural Solutions">
            {/* SEO-Rich Text for Crawlers (Hidden) */}
            <div className="visually-hidden" aria-hidden="true">
              <p>Krishidnya provides agricultural technology solutions including precision farming, smart irrigation, soil testing, crop management, and sustainable agriculture practices for Indian farmers. We offer agritech solutions and farming equipment.</p>
            </div>

            {/* Premium Menu Toggle */}
            <button
              className="menu-toggle"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close navigation menu - Agricultural services' : 'Open navigation menu - Agricultural services'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              type="button"
            >
              <span className="menu-icon">
                <span className="menu-line"></span>
                <span className="menu-line"></span>
                <span className="menu-line"></span>
              </span>
            </button>

            {/* Premium Logo with Animation - Updated Background */}
            <Link
              to="/"
              className="logo-link"
              onClick={() => setIsMenuOpen(false)}
              aria-label="Krishidnya Agricultural Solutions - Premium Farming Technology - Return to Homepage"
              itemProp="url"
            >
              <meta itemProp="name" content="Krishidnya Agricultural Solutions" />
              <div className="logo-wrapper">
                <img
                  src={logo}
                  alt="Krishidnya - India's Leading Agricultural Technology Company | Smart Farming Solutions | Sustainable Agriculture Expert | Precision Farming | Agritech Solutions"
                  className="logo-image"
                  itemProp="logo"
                  title="Krishidnya Agricultural Solutions - Transforming Indian Agriculture with Technology"
                  width="160"
                  height="64"
                  loading="eager"
                />
                <div className="logo-background"></div>
                <div className="logo-shimmer"></div>
              </div>
            </Link>

            {/* Premium Desktop Navigation */}
            <div className="nav-desktop">
              {navLinks.map((link, index) => (
                <div key={link.name} className="nav-item" style={{ animationDelay: `${index * 0.1}s` }}>
                  <Link
                    to={link.path}
                    className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                    title={link.title}
                    aria-label={link.description}
                    aria-current={location.pathname === link.path ? 'page' : undefined}
                    itemProp="url"
                    data-keywords={link.keywords}
                  >
                    <span className="nav-text" itemProp="name">{link.name}</span>
                    <span className="nav-underline"></span>
                    <span className="nav-dot"></span>
                  </Link>
                </div>
              ))}
            </div>

            {/* Spacer for mobile centering */}
            <div className="nav-spacer" aria-hidden="true" />
          </nav>
        </div>
        <div className="header-border"></div>
      </header>

      {/* Premium Mobile Navigation */}
      <div
        id="mobile-menu"
        className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile Navigation - Agricultural Services & Solutions"
      >
        <div className="mobile-menu-content">
          <div className="mobile-header">
            <Link
              to="/"
              className="mobile-logo-link"
              onClick={() => setIsMenuOpen(false)}
              aria-label="Return to Homepage"
            >
              <div className="mobile-logo-wrapper">
                <img
                  src={logo}
                  alt="Krishidnya Agricultural Solutions - Smart Farming Technology | Precision Agriculture | Agritech Innovation"
                  className="mobile-logo"
                  title="Krishidnya - Agricultural Innovation Leader"
                  width="130"
                  height="52"
                  loading="eager"
                />
              </div>
            </Link>
            <button
              className="close-button"
              onClick={() => setIsMenuOpen(false)}
              aria-label="Close mobile navigation menu"
              type="button"
            >
              <FaTimes aria-hidden="true" />
            </button>
          </div>

          <nav className="mobile-nav" aria-label="Mobile navigation links - Agricultural solutions">
            {navLinks.map((link, index) => (
              <Link
                key={link.name}
                to={link.path}
                className={`mobile-link ${location.pathname === link.path ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
                title={link.title}
                aria-label={link.description}
                aria-current={location.pathname === link.path ? 'page' : undefined}
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                <span className="mobile-link-text">{link.name}</span>
                <span className="mobile-link-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </Link>
            ))}
          </nav>

          {/* Mobile Footer */}
          <div className="mobile-footer">
            <div className="footer-content">
              <h3 className="footer-title">Krishidnya Agricultural Solutions</h3>
              <p className="footer-tagline">
                🌾 Empowering Farmers With Technology & Innovation
              </p>
              {/* Hidden SEO keywords */}
              <p className="footer-keywords visually-hidden">
                agricultural technology, precision farming, sustainable agriculture, crop management, soil testing, smart farming, agritech solutions, farming equipment, agricultural consulting, organic farming India
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Premium Overlay */}
      {isMenuOpen && (
        <div
          className="overlay"
          onClick={() => setIsMenuOpen(false)}
          aria-hidden="true"
          role="presentation"
        />
      )}

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Source+Serif+Pro:wght@400;600;700&family=Inter:wght@300;400;500;600;700&display=swap');

        /* Using same variables as Home page with enhanced sizes */
        :root {
          --primary-black: #000000;
          --secondary-black: #1A1A1A;
          --medium-black: #333333;
          --light-black: #666666;
          --primary-green: #2E8B57;
          --secondary-green: #3A7D34;
          --light-green: #4CAF7A;
          --accent-green: #2D5A27;
          --white: #FFFFFF;
          --off-white: #FAFAFA;
          --light-gray: #F5F5F5;
          --medium-gray: #E8E8E8;
          --border-color: #E0E0E0;
          --text-primary: #000000;
          --text-secondary: #333333;
          --text-light: #666666;
          --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.04);
          --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08);
          --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.12);
          --radius-sm: 6px;
          --radius-md: 12px;
          --radius-lg: 16px;
          --radius-xl: 20px;
          --transition: all 0.3s ease;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .visually-hidden {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }

        /* Header - Increased height */
        .header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: var(--white);
          z-index: 1000;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          border-bottom: 1px solid transparent;
        }

        .header.scrolled {
          background: var(--white);
          box-shadow: var(--shadow-md);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-bottom: 1px solid var(--border-color);
        }

        .header-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 28px;
          position: relative;
          z-index: 2;
        }

        /* Navbar - Increased height */
        .navbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: 90px;
          position: relative;
        }

        /* Logo - Increased size */
        .logo-link {
          display: flex;
          align-items: center;
          text-decoration: none;
          z-index: 2;
          position: relative;
          border-radius: var(--radius-md);
          overflow: hidden;
          padding: 6px;
        }

        .logo-wrapper {
          position: relative;
          padding: 6px;
          border-radius: var(--radius-md);
          background: transparent;
        }

        .logo-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: var(--white);
          border-radius: var(--radius-md);
          opacity: 0.9;
          transition: var(--transition);
        }

        .logo-link:hover .logo-background {
          background: var(--off-white);
          opacity: 1;
        }

        .logo-image {
          height: 68px;
          width: auto;
          object-fit: contain;
          position: relative;
          z-index: 2;
          transition: var(--transition);
          filter: none;
        }

        .logo-link:hover .logo-image {
          transform: scale(1.05);
        }

        .logo-shimmer {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(46, 139, 87, 0.1), transparent);
          animation: shimmer 3s infinite;
          pointer-events: none;
          z-index: 1;
          border-radius: var(--radius-md);
        }

        @keyframes shimmer {
          0% { left: -100%; }
          50% { left: 100%; }
          100% { left: 100%; }
        }

        /* Desktop Navigation - Increased sizes */
        .nav-desktop {
          display: flex;
          gap: 6px;
          align-items: center;
        }

        .nav-item {
          opacity: 0;
          animation: fadeInNav 0.6s ease forwards;
        }

        @keyframes fadeInNav {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .nav-link {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 16px;
          font-weight: 500;
          color: var(--text-secondary);
          text-decoration: none;
          padding: 14px 24px;
          border-radius: var(--radius-md);
          transition: var(--transition);
          position: relative;
          letter-spacing: 0.3px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }

        .nav-text {
          position: relative;
          z-index: 1;
          transition: var(--transition);
          font-size: 16px;
        }

        .nav-underline {
          position: absolute;
          bottom: 10px;
          left: 24px;
          right: 24px;
          height: 2.5px;
          background: linear-gradient(90deg, var(--primary-green), var(--light-green));
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 1px;
        }

        .nav-dot {
          position: absolute;
          top: 8px;
          right: 16px;
          width: 5px;
          height: 5px;
          background: var(--primary-green);
          border-radius: 50%;
          opacity: 0;
          transform: scale(0);
          transition: var(--transition);
        }

        .nav-link::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(46, 139, 87, 0.05);
          border-radius: var(--radius-md);
          opacity: 0;
          transform: scale(0.95);
          transition: var(--transition);
        }

        .nav-link:hover {
          color: var(--primary-green);
          transform: translateY(-2px);
        }

        .nav-link:hover::before {
          opacity: 1;
          transform: scale(1);
        }

        .nav-link:hover .nav-underline {
          transform: scaleX(1);
        }

        .nav-link.active {
          color: var(--primary-green);
          font-weight: 600;
          background: rgba(46, 139, 87, 0.1);
        }

        .nav-link.active .nav-underline {
          transform: scaleX(1);
        }

        .nav-link.active .nav-dot {
          opacity: 1;
          transform: scale(1);
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(46, 139, 87, 0.4); }
          50% { box-shadow: 0 0 0 6px rgba(46, 139, 87, 0); }
        }

        /* Menu Toggle - Increased size */
        .menu-toggle {
          display: none;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 12px;
          border-radius: var(--radius-md);
          transition: var(--transition);
          z-index: 2;
        }

        .menu-icon {
          display: flex;
          flex-direction: column;
          gap: 5px;
          width: 28px;
        }

        .menu-line {
          width: 100%;
          height: 2.5px;
          background: var(--text-secondary);
          border-radius: 1px;
          transition: var(--transition);
        }

        .menu-toggle:hover {
          background: rgba(46, 139, 87, 0.1);
        }

        .menu-toggle:hover .menu-line {
          background: var(--primary-green);
        }

        .nav-spacer {
          width: 60px;
        }

        /* Mobile Menu - Slightly larger */
        .mobile-menu {
          position: fixed;
          top: 0;
          left: -100%;
          width: 100%;
          max-width: 340px;
          height: 100vh;
          background: var(--white);
          z-index: 1001;
          transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: var(--shadow-lg);
          overflow-y: auto;
          border-right: 1px solid var(--border-color);
        }

        .mobile-menu.open {
          left: 0;
        }

        .mobile-menu-content {
          height: 100%;
          display: flex;
          flex-direction: column;
          position: relative;
        }

        .mobile-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 22px 26px;
          background: var(--white);
          border-bottom: 1px solid var(--border-color);
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .mobile-logo-link {
          display: flex;
          align-items: center;
          text-decoration: none;
          border-radius: var(--radius-md);
          padding: 6px;
          background: transparent;
          transition: var(--transition);
        }

        .mobile-logo-link:hover {
          background: var(--off-white);
        }

        .mobile-logo-wrapper {
          position: relative;
          padding: 6px;
          border-radius: var(--radius-md);
          background: var(--white);
        }

        .mobile-logo {
          height: 56px;
          width: auto;
          object-fit: contain;
          filter: none;
        }

        .close-button {
          background: var(--white);
          border: 1px solid var(--border-color);
          color: var(--text-secondary);
          font-size: 20px;
          cursor: pointer;
          padding: 12px;
          width: 46px;
          height: 46px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: var(--radius-md);
          transition: var(--transition);
        }

        .close-button:hover {
          background: rgba(46, 139, 87, 0.1);
          color: var(--primary-green);
          border-color: var(--primary-green);
          transform: rotate(90deg);
        }

        .mobile-nav {
          flex: 1;
          padding: 22px 26px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .mobile-link {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 17px;
          font-weight: 500;
          color: var(--text-secondary);
          text-decoration: none;
          padding: 16px 20px;
          border-radius: var(--radius-md);
          transition: var(--transition);
          background: var(--white);
          border: 1px solid var(--border-color);
          display: flex;
          justify-content: space-between;
          align-items: center;
          letter-spacing: 0.3px;
          opacity: 0;
          animation: slideInMobile 0.4s ease forwards;
        }

        @keyframes slideInMobile {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }

        .mobile-link-icon {
          width: 22px;
          height: 22px;
          color: var(--text-light);
          transition: var(--transition);
        }

        .mobile-link:hover {
          background: rgba(46, 139, 87, 0.1);
          color: var(--primary-green);
          border-color: var(--primary-green);
          transform: translateX(6px);
        }

        .mobile-link:hover .mobile-link-icon {
          color: var(--primary-green);
          transform: translateX(6px);
        }

        .mobile-link.active {
          background: rgba(46, 139, 87, 0.15);
          color: var(--primary-green);
          border-color: var(--primary-green);
          font-weight: 600;
          border-left: 5px solid var(--primary-green);
        }

        .mobile-link.active .mobile-link-icon {
          color: var(--primary-green);
        }

        /* Mobile Footer - Slightly larger */
        .mobile-footer {
          padding: 22px 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 110px;
          background: var(--off-white);
          border-top: 1px solid var(--border-color);
        }

        .footer-content {
          background: transparent;
          padding: 0;
          border: none;
          box-shadow: none;
          text-align: center;
          max-width: 280px;
        }

        .footer-title {
          font-family: 'Source Serif Pro', serif;
          font-size: 15px;
          font-weight: 600;
          color: var(--primary-green);
          margin-bottom: 8px;
          letter-spacing: 0.5px;
        }

        .footer-tagline {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 400;
          color: var(--text-secondary);
          line-height: 1.5;
        }

        .footer-keywords {
          margin-top: 8px;
        }

        /* Header Border - More prominent */
        .header-border {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent 0%, var(--primary-green) 50%, transparent 100%);
          opacity: 0;
          transition: opacity 0.5s ease;
        }

        .header.scrolled .header-border {
          opacity: 0.4;
        }

        /* Overlay */
        .overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          z-index: 1000;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        /* Responsive Design with increased sizes */
        @media (max-width: 1024px) {
          .nav-link {
            padding: 12px 20px;
            font-size: 15px;
          }
        }

        @media (max-width: 768px) {
          .nav-desktop {
            display: none;
          }
          
          .menu-toggle {
            display: flex;
          }
          
          .logo-link {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
          }
          
          .navbar {
            height: 80px;
          }
          
          .header-container {
            padding: 0 22px;
          }
          
          .logo-image {
            height: 56px;
          }
        }

        @media (max-width: 480px) {
          .mobile-menu {
            max-width: 320px;
          }
          
          .mobile-header {
            padding: 20px 24px;
          }
          
          .mobile-nav {
            padding: 20px 24px;
          }
          
          .mobile-link {
            padding: 14px 18px;
            font-size: 16px;
          }
          
          .mobile-footer {
            padding: 20px 24px;
            min-height: 100px;
          }
          
          .header-container {
            padding: 0 18px;
          }
          
          .navbar {
            height: 76px;
          }
          
          .logo-image {
            height: 52px;
          }
        }

        @media (max-width: 360px) {
          .mobile-menu {
            max-width: 300px;
          }
          
          .mobile-footer {
            min-height: 90px;
          }
          
          .navbar {
            height: 72px;
          }
          
          .logo-image {
            height: 48px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* Accessibility */
        .nav-link:focus-visible,
        .mobile-link:focus-visible,
        .menu-toggle:focus-visible,
        .close-button:focus-visible,
        .logo-link:focus-visible {
          outline: 2px solid var(--primary-green);
          outline-offset: 3px;
          border-radius: var(--radius-md);
        }

        /* Touch device optimizations */
        @media (hover: none) and (pointer: coarse) {
          .nav-link, .mobile-link, .menu-toggle, .close-button {
            min-height: 48px;
            min-width: 48px;
          }
        }
      `}</style>
    </>
  );
};

export default Header;
